import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   asString,
   formatDate as formatIsoDate
} from '../utils/report-formatting.utils';
import { parseReportJson } from '../utils/report-json.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

type OutOfOrderRoomRow = {
   rowId: string;
   room: string;
   type: string;
   reason: string;
   reasonCode: string;
   description: string;
   from: string;
   fromRaw: string;
   to: string;
   toRaw: string;
   returnStatus: string;
   remarks: string;
   status: string;
   completed: string;
   hasRealTyallsRowId: boolean;
};

function formatOutOfOrderDate(value: unknown): string {
   const rawValue = asString(value).trim();

   if (!rawValue) {
      return '';
   }

   const formattedIsoDate = formatIsoDate(rawValue);

   if (formattedIsoDate !== rawValue) {
      return formattedIsoDate;
   }

   const slashDateMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);

   if (slashDateMatch) {
      return `${slashDateMatch[1].padStart(
         2,
         '0'
      )}/${slashDateMatch[2].padStart(2, '0')}/${slashDateMatch[3]}`;
   }

   return rawValue;
}

/**
 * Row ID rules:
 * - Prefer real TyallsRowId because row annotations depend on it.
 * - Fall back to Id/id.
 * - As a last resort, generate a deterministic frontend-only ID.
 */
function getRowId(row: any): string {
   const realTyallsRowId = getRealTyallsRowId(row);

   if (realTyallsRowId) {
      return realTyallsRowId;
   }

   return (
      asString(row?.Id ?? row?.id) ||
      `out-of-order-room:${asString(row?.RoomNumber)}:${asString(
         row?.ReasonCode
      )}:${asString(row?.From ?? row?.Start)}:${asString(row?.To ?? row?.End)}`
   );
}

function getReasonDisplay(row: any): string {
   const reasonCode = asString(row?.ReasonCode);
   const description = asString(row?.Description ?? row?.ReasonDescription);

   if (reasonCode && description) {
      return `${reasonCode}\n${description}`;
   }

   if (description) {
      return description;
   }

   return reasonCode;
}

function getSourceRows(reportJson: unknown): any[] {
   const parsedReportJson = parseReportJson(reportJson);

   /**
    * New Task Generator shape:
    * {
    *   OutOfOrderRooms: [
    *     {
    *       RoomNumber,
    *       RoomType,
    *       ReasonCode,
    *       Description,
    *       From,
    *       To,
    *       ReturnStatus,
    *       Remarks,
    *       Status,
    *       TyallsRowId
    *     }
    *   ]
    * }
    */
   const outOfOrderRooms = parsedReportJson['OutOfOrderRooms'];

   if (Array.isArray(outOfOrderRooms)) {
      return outOfOrderRooms;
   }

   /**
    * Backward compatibility with older nested shape:
    * {
    *   Rooms: [
    *     {
    *       RoomNumber,
    *       RoomType,
    *       OutOfOrderValues: [...]
    *     }
    *   ]
    * }
    */
   const rooms = parsedReportJson['Rooms'];

   if (Array.isArray(rooms)) {
      const rows: any[] = [];

      for (const room of rooms) {
         const values = Array.isArray(room?.OutOfOrderValues)
            ? room.OutOfOrderValues
            : [];

         for (const value of values) {
            rows.push({
               ...value,
               RoomNumber: room?.RoomNumber,
               RoomType: room?.RoomType,
               Description: value?.ReasonDescription,
               From: value?.Start,
               To: value?.End,
               ReturnStatus: value?.ReturnStatus ?? value?.HousekeepingStatus,
               Remarks: value?.Remarks ?? value?.Remark,
               Status: value?.Status,
               Completed: value?.Completed
            });
         }
      }

      return rows;
   }

   return [];
}

function normaliseRows(reportJson: unknown): OutOfOrderRoomRow[] {
   return getSourceRows(reportJson).map((row) => {
      const fromRaw = asString(row?.From ?? row?.Start);
      const toRaw = asString(row?.To ?? row?.End);

      return {
         rowId: getRowId(row),
         room: asString(row?.RoomNumber),
         type: asString(row?.RoomType),
         reason: getReasonDisplay(row),
         reasonCode: asString(row?.ReasonCode),
         description: asString(row?.Description ?? row?.ReasonDescription),
         from: formatOutOfOrderDate(fromRaw),
         fromRaw,
         to: formatOutOfOrderDate(toRaw),
         toRaw,
         returnStatus: asString(row?.ReturnStatus ?? row?.HousekeepingStatus),
         remarks: asString(row?.Remarks ?? row?.Remark),
         status: asString(row?.Status),
         completed: asString(row?.Completed),
         hasRealTyallsRowId: !!getRealTyallsRowId(row)
      };
   });
}

function sortRows(rows: OutOfOrderRoomRow[]): OutOfOrderRoomRow[] {
   return [...rows].sort((a, b) => {
      const aRoom = Number(a.room);
      const bRoom = Number(b.room);

      if (Number.isFinite(aRoom) && Number.isFinite(bRoom)) {
         return aRoom - bRoom;
      }

      return a.room.localeCompare(b.room);
   });
}

export const OUT_OF_ORDER_ROOMS_DEFINITION: ReportDefinition = {
   reportType: 'OutOfOrderByReason',
   version: 4,
   title: 'Out of Order Rooms',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Out of Order Rooms'
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['outOfOrderRoom']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['outOfOrderRoom'],
      disabledMessage:
         'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
   },

   columns: [
      {
         key: 'room',
         header: 'ROOM',
         width: '90px',
         sort: { type: 'string' }
      },
      {
         key: 'type',
         header: 'TYPE',
         width: '90px',
         sort: { type: 'string' }
      },
      {
         key: 'reason',
         header: 'REASON',
         width: '170px',
         maxChars: 80,
         sort: { type: 'string' }
      },
      {
         key: 'from',
         header: 'FROM',
         width: '120px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['fromRaw']
         }
      },
      {
         key: 'to',
         header: 'TO',
         width: '120px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['toRaw']
         }
      },
      {
         key: 'returnStatus',
         header: 'RETURN ST.',
         width: '130px',
         sort: { type: 'string' }
      },
      {
         key: 'remarks',
         header: 'REMARKS',
         width: '360px',
         maxChars: 140,
         sort: { type: 'string' }
      },
      {
         key: 'status',
         header: 'STATUS',
         width: '130px',
         sort: { type: 'string' }
      },
      {
         key: 'completed',
         header: 'COMPLETED',
         width: '130px',
         sort: { type: 'string' }
      }
   ],

   filter: {
      fields: [
         { key: 'room', label: 'Room', type: 'string' },
         { key: 'type', label: 'Type', type: 'string' },
         { key: 'reasonCode', label: 'Reason Code', type: 'string' },
         { key: 'description', label: 'Description', type: 'string' },
         {
            key: 'from',
            label: 'From',
            type: 'date',
            getValue: (row) => row.cells['fromRaw']
         },
         {
            key: 'to',
            label: 'To',
            type: 'date',
            getValue: (row) => row.cells['toRaw']
         },
         {
            key: 'returnStatus',
            label: 'Return Status',
            type: 'string'
         },
         { key: 'remarks', label: 'Remarks', type: 'string' },
         { key: 'status', label: 'Status', type: 'string' },
         { key: 'completed', label: 'Completed', type: 'string' }
      ]
   },

   flattenRows: (reportJson: unknown): ReportRowVm[] => {
      const rows = sortRows(normaliseRows(reportJson));

      return rows.map((row) => ({
         rowId: row.rowId,
         isAnnotatable: row.hasRealTyallsRowId,
         cells: {
            rowType: 'outOfOrderRoom',
            room: row.room,
            type: row.type,
            reason: row.reason,
            reasonCode: row.reasonCode,
            description: row.description,
            from: row.from,
            fromRaw: row.fromRaw,
            to: row.to,
            toRaw: row.toRaw,
            returnStatus: row.returnStatus,
            remarks: row.remarks,
            status: row.status,
            completed: row.completed
         }
      }));
   }
};
